'use client';

import { Button } from '@common/components';
import { cn } from '@common/utils';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import CountUp from 'react-countup';

import { UnauthQueryOptions } from '@/api/v2';
import { CustomLink } from '@/components/atom/CustomLink';
import { BottomNavPortal } from '@/components/client';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

interface NonMemberContentsBlockProps {
  contentType: 'policy' | 'info' | 'talk';
  isTalkDetail?: boolean;
}

const CONTENT_CONFIG = {
  policy: {
    title: '지금 보는 정책이 궁금하다면?',
    descriptionPrefix: '로그인 후 정책 확인하고,',
    descriptionSuffix: '받아보세요 !',
    imageSrc: 'policyQuestion',
    imageAlt: '정책',
  },
  info: {
    title: '지금 보는 소식이 궁금하다면?',
    descriptionPrefix: '로그인 후 소식 확인하고,',
    descriptionSuffix: '받아보세요 !',
    imageSrc: 'infoQuestion',
    imageAlt: '소식',
  },
  talk: {
    title: '다른 사람들의 이야기가 궁금하다면?',
    descriptionPrefix: '로그인 후 최신 피드를 확인하고,',
    descriptionSuffix: '받아보세요 !',
    imageSrc: 'talkQuestion',
    imageAlt: '커뮤니티 수다',
  },
};

const fadeInUpVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

const imageVariants = {
  initial: { scale: 0.95, opacity: 0.9 },
  hover: {
    scale: 1.05,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 15,
    },
  },
};

export const NonMemberContentsBlock = ({
  contentType,
  isTalkDetail = false,
}: NonMemberContentsBlockProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const pathname = usePathname();
  const router = useCustomRouter();
  const { data: pointJoinData } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/point/info']({
      query: {
        activity_detail_type: 'JOIN',
      },
    }),
    select: ({ context }) => context,
  });

  const { data: pointMemberFilterData } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/point/info']({
      query: {
        activity_detail_type: 'MEMBER_FILTER',
      },
    }),
    select: ({ context }) => context,
  });

  const rewardPoint =
    (pointJoinData?.point ?? 0) + (pointMemberFilterData?.point ?? 0);

  useEffect(() => {
    if (isTalkDetail && isLogin === false) {
      router.replace(
        ROUTES.LOGIN.withSearchParams({
          searchParams: { redirect: pathname ?? '' },
        }),
      );
    }
  }, [isTalkDetail, pathname, router, isLogin]);

  if (isLogin) return <></>;

  const contentItem = CONTENT_CONFIG[contentType];

  return (
    <BottomNavPortal>
      <motion.div
        className="absolute bottom-0 z-10 flex w-full flex-col items-center gap-24 py-40"
        style={{
          background:
            'linear-gradient(to top, white 50%, rgba(255,255,255,0.9) 80%, rgba(255,255,255,0) 100%)',
        }}
        initial="hidden"
        animate="visible"
        variants={fadeInUpVariants}
      >
        <div
          className={cn('fixed inset-0', {
            'top-56': contentType !== 'talk',
            'top-120': contentType === 'talk',
          })}
        />
        <div className="flex flex-col items-center gap-8">
          <motion.div
            initial="initial"
            whileHover="hover"
            animate="initial"
            variants={imageVariants}
          >
            <Image
              src={`/assets/pngs/block/${contentItem.imageSrc}.png`}
              alt={contentItem.imageAlt}
              width={70}
              height={70}
            />
          </motion.div>

          <motion.div
            className="font-700 typo-body2"
            variants={fadeInUpVariants}
            transition={{ delay: 0.1 }}
          >
            {contentItem.title}
          </motion.div>

          <motion.p
            className="text-center font-500 text-Gray-600 typo-body4"
            variants={fadeInUpVariants}
            transition={{ delay: 0.2 }}
          >
            {contentItem.descriptionPrefix}
            <br />
            바로 출금 가능한{' '}
            <span className="text-B-500">
              <CountUp preserveValue duration={0.7} end={rewardPoint} />P
            </span>{' '}
            {contentItem.descriptionSuffix}
          </motion.p>
        </div>

        <motion.div
          variants={fadeInUpVariants}
          transition={{ delay: 0.3 }}
          className="z-10"
        >
          <Button dimensions="H48" theme="secondary-fill">
            <CustomLink
              href={ROUTES.LOGIN.withSearchParams({
                searchParams: { redirect: pathname ?? '' },
              })}
            >
              회원 가입하기
            </CustomLink>
          </Button>
        </motion.div>
      </motion.div>
    </BottomNavPortal>
  );
};
